import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import useUsersResource from "../../resources/useUsersResource";

interface UniverseButtonProps {
  onUniverseMatch: () => void;
  isLoading: boolean;
  disabled: boolean;
}

const UniverseButton: React.FC<UniverseButtonProps> = ({
  onUniverseMatch,
  isLoading,
  disabled,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { universeMatch } = useUsersResource();
  const [error, setError] = useState<string>("");

  const handleUniverseMatch = useCallback(async () => {
    if (disabled || isLoading) return;

    setError("");
    onUniverseMatch(); // Notify parent component that universe match started

    try {
      const user = await universeMatch();
      const { handle: foundHandle } = user;

      // Navigate to Sangati page with the matched user
      navigate("/sangati", {
        state: { matcheeHandle: foundHandle },
        replace: true,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        const code = error.response?.data?.code;
        if (code === 101) {
          setError(t("loginRequired", "Login required"));
          return;
        }
        if (code === 102) {
          setError(t("noMatchPossible", "No match possible"));
          return;
        }
      }

      setError(t("noMatchPossible", "No match possible"));
    }
  }, [universeMatch, navigate, onUniverseMatch, disabled, isLoading, t]);

  const handleClick = () => {
    if (!disabled && !isLoading) {
      handleUniverseMatch();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === " ") && !disabled && !isLoading) {
      e.preventDefault();
      handleUniverseMatch();
    }
  };

  return (
    <div className="mb-3 sm:mb-4">
      <div className="mt-4 flex justify-center">
        <button
          className={`text-14 font-medium underline transition-colors ${
            disabled || isLoading
              ? "opacity-50 cursor-not-allowed pointer-events-none"
              : "cursor-pointer"
          }`}
          onClick={handleClick}
          disabled={disabled || isLoading}
          aria-label={t("universe", "Universe, find me someone!")}
          tabIndex={disabled || isLoading ? -1 : 0}
          type="button"
          onKeyDown={handleKeyDown}
        >
          {t("universe")}
        </button>
      </div>

      {error && (
        <div className="mt-2 text-sangati-orange text-14 sm:text-16 text-center font-texgyrepagella">
          {error}
        </div>
      )}
    </div>
  );
};

export default UniverseButton;
